import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './InsightsTab.css';

const STAGE_ORDER = [
  'Order Received',
  'Art/Design',
  'Screen Setup',
  'Printing',
  'Quality Check',
  'Ready for Pickup'
];

const InsightsTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/kerrscreen/analytics');
      setAnalytics(response.data.analytics);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="insights-loading">
        <div className="loading-spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="insights-error">
        <p>{error}</p>
        <button onClick={fetchAnalytics}>Retry</button>
      </div>
    );
  }

  if (!analytics) {
    return <div className="insights-empty">No analytics data available</div>;
  }

  const { timePerStage, throughput, completionTime, repeatCustomers, revenue, garmentTypes, bottleneck } = analytics;

  // Find the current bottleneck (stage with most jobs)
  const currentBottleneck = bottleneck && bottleneck.length > 0 ? bottleneck[0] : null;

  // Calculate throughput trend
  const recentThroughput = throughput && throughput.length >= 2
    ? throughput[0]?.completed_count || 0
    : 0;
  const previousThroughput = throughput && throughput.length >= 2
    ? throughput[1]?.completed_count || 0
    : 0;
  const throughputTrend = previousThroughput > 0
    ? ((recentThroughput - previousThroughput) / previousThroughput * 100).toFixed(0)
    : 0;

  return (
    <div className="insights-container">
      {/* Summary Cards */}
      <div className="insights-summary">
        <div className="summary-card completion-time">
          <div className="card-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div className="card-content">
            <span className="card-value">{completionTime.avg_days || '—'}</span>
            <span className="card-label">Avg Days to Complete</span>
            <span className="card-detail">
              Range: {completionTime.min_days || 0} - {completionTime.max_days || 0} days
            </span>
          </div>
        </div>

        <div className="summary-card throughput">
          <div className="card-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <div className="card-content">
            <span className="card-value">{recentThroughput}</span>
            <span className="card-label">Jobs This Week</span>
            <span className={`card-trend ${parseInt(throughputTrend) >= 0 ? 'positive' : 'negative'}`}>
              {parseInt(throughputTrend) >= 0 ? '+' : ''}{throughputTrend}% vs last week
            </span>
          </div>
        </div>

        <div className="summary-card revenue">
          <div className="card-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div className="card-content">
            <span className="card-value">${parseFloat(revenue.total_revenue || 0).toLocaleString()}</span>
            <span className="card-label">Revenue (90 days)</span>
            <span className="card-detail">
              Avg job: ${parseFloat(revenue.avg_job_value || 0).toFixed(2)}
            </span>
          </div>
        </div>

        {currentBottleneck && (
          <div className="summary-card bottleneck">
            <div className="card-icon warning">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div className="card-content">
              <span className="card-value">{currentBottleneck.status}</span>
              <span className="card-label">Current Bottleneck</span>
              <span className="card-detail">
                {currentBottleneck.count} jobs waiting
                {currentBottleneck.overdue_count > 0 && (
                  <span className="overdue-badge">{currentBottleneck.overdue_count} overdue</span>
                )}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Time Per Stage */}
      <div className="insights-section">
        <h3>Time Per Stage</h3>
        <p className="section-description">Average time jobs spend in each production stage</p>
        <div className="stage-times">
          {STAGE_ORDER.map(stageName => {
            const stageData = timePerStage.find(s => s.stage === stageName);
            const avgDays = stageData ? parseFloat(stageData.avg_days) : 0;
            const maxDays = Math.max(...timePerStage.map(s => parseFloat(s.avg_days) || 0), 1);
            const barWidth = avgDays > 0 ? (avgDays / maxDays) * 100 : 0;

            return (
              <div key={stageName} className="stage-row">
                <span className="stage-name">{stageName}</span>
                <div className="stage-bar-container">
                  <div
                    className={`stage-bar ${avgDays > 2 ? 'slow' : avgDays > 1 ? 'moderate' : 'fast'}`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <span className="stage-value">
                  {stageData ? `${stageData.avg_days} days` : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="insights-columns">
        {/* Throughput Chart */}
        <div className="insights-section">
          <h3>Weekly Throughput</h3>
          <p className="section-description">Jobs completed per week</p>
          <div className="throughput-chart">
            {throughput && throughput.slice(0, 8).reverse().map((week, index) => {
              const maxCount = Math.max(...throughput.map(w => parseInt(w.completed_count) || 0), 1);
              const barHeight = (parseInt(week.completed_count) / maxCount) * 100;
              const weekDate = new Date(week.week_start);
              const weekLabel = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

              return (
                <div key={index} className="throughput-bar-container">
                  <div className="throughput-bar" style={{ height: `${barHeight}%` }}>
                    <span className="bar-value">{week.completed_count}</span>
                  </div>
                  <span className="bar-label">{weekLabel}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Garment Types */}
        <div className="insights-section">
          <h3>Job Types</h3>
          <p className="section-description">Most common garment types (90 days)</p>
          <div className="garment-types">
            {garmentTypes && garmentTypes.map((type, index) => {
              const maxCount = garmentTypes[0]?.count || 1;
              const percentage = (type.count / maxCount) * 100;

              return (
                <div key={index} className="garment-row">
                  <span className="garment-name">{type.garment_type}</span>
                  <div className="garment-bar-container">
                    <div className="garment-bar" style={{ width: `${percentage}%` }} />
                  </div>
                  <span className="garment-count">{type.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Repeat Customers */}
      {repeatCustomers && repeatCustomers.length > 0 && (
        <div className="insights-section">
          <h3>Repeat Customers</h3>
          <p className="section-description">Customers with multiple orders</p>
          <div className="repeat-customers">
            {/* Desktop Table View */}
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Orders</th>
                  <th>Total Revenue</th>
                  <th>Last Order</th>
                </tr>
              </thead>
              <tbody>
                {repeatCustomers.map((customer, index) => (
                  <tr key={index}>
                    <td className="customer-name">{customer.customer_name}</td>
                    <td className="job-count">{customer.job_count}</td>
                    <td className="revenue">${parseFloat(customer.total_revenue || 0).toLocaleString()}</td>
                    <td className="last-order">
                      {new Date(customer.last_order).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className="repeat-customers-mobile">
              {repeatCustomers.map((customer, index) => (
                <div key={index} className="customer-card">
                  <div className="customer-card-header">
                    <span className="customer-card-name">{customer.customer_name}</span>
                    <span className="customer-card-orders">{customer.job_count} orders</span>
                  </div>
                  <div className="customer-card-body">
                    <span className="customer-card-revenue">${parseFloat(customer.total_revenue || 0).toLocaleString()}</span>
                    <span className="customer-card-date">Last: {new Date(customer.last_order).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stage Workload */}
      <div className="insights-section">
        <h3>Current Stage Workload</h3>
        <p className="section-description">Active jobs by production stage</p>
        <div className="workload-grid">
          {bottleneck && bottleneck.map((stage, index) => (
            <div key={index} className={`workload-card ${stage.overdue_count > 0 ? 'has-overdue' : ''}`}>
              <span className="workload-stage">{stage.status}</span>
              <span className="workload-count">{stage.count}</span>
              {stage.overdue_count > 0 && (
                <span className="workload-overdue">{stage.overdue_count} overdue</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InsightsTab;
